import React, { useState } from 'react';
import { makeStyles, Radio } from '@fluentui/react-components';
import { LikertQuestion as LikertQuestionType } from '../../../../types/forms';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
  },
  questionTitle: {
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '20px',
    fontWeight: 400,
    lineHeight: '28px',
    color: '#333333',
    marginBottom: '8px',
    paddingLeft: '0px',
  },
  likertGrid: {
    paddingLeft: '0px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  gridHeader: {
    display: 'grid',
    gridTemplateColumns: '200px repeat(auto-fit, minmax(80px, 1fr))',
    height: '45px',
    alignItems: 'center',
  },
  headerLabel: {
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '14px',
    fontWeight: 400,
    color: '#333333',
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  gridRow: {
    display: 'grid',
    gridTemplateColumns: '200px repeat(auto-fit, minmax(80px, 1fr))',
    height: '45px',
    alignItems: 'center',
  },
  gridRowEven: {
    backgroundColor: '#fafafa',
  },
  statementLabel: {
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '14px',
    color: '#333333',
    lineHeight: '19px',
    paddingLeft: '20px',
  },
  radioCell: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
});

interface LikertQuestionViewProps {
  question: LikertQuestionType;
  questionNumber: number;
  onClick?: () => void;
}

export const LikertQuestionView: React.FC<LikertQuestionViewProps> = ({
  question,
  questionNumber,
  onClick,
}) => {
  const styles = useStyles();
  const [answers, setAnswers] = useState<Record<number, string>>({});

  const handleAnswerChange = (statementIndex: number, value: string) => {
    setAnswers({ ...answers, [statementIndex]: value });
  };

  return (
    <div className={styles.container} onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }} data-question-view>
      <div className={styles.questionTitle}>
        {questionNumber}. {question.title}
        {question.required && <span style={{ color: '#d13438', marginLeft: '4px' }}>*</span>}
      </div>
      
      <div className={styles.likertGrid}>
        {/* Header */}
        <div className={styles.gridHeader}>
          <div /> {/* Empty cell for statement column */}
          {Array.from({ length: (question.scale?.max ?? 5) - (question.scale?.min ?? 1) + 1 }, (_, i) => i + (question.scale?.min ?? 1)).map((num, index) => {
            let label = '';
            if (question.scale?.labels && question.scale.labels[index]) {
              label = question.scale.labels[index];
            } else if (index === 0 && question.scale?.minLabel) {
              label = question.scale.minLabel;
            } else if (index === ((question.scale?.max ?? 5) - (question.scale?.min ?? 1)) && question.scale?.maxLabel) {
              label = question.scale.maxLabel;
            } else {
              label = `Option ${num}`;
            }
            return (
              <div key={index} className={styles.headerLabel}>
                {label}
              </div>
            );
          })}
        </div>

        {/* Statement Rows */}
        {question.statements.map((statement, rowIndex) => (
          <div key={rowIndex} className={`${styles.gridRow} ${rowIndex % 2 === 0 ? styles.gridRowEven : ''}`}>
            <div className={styles.statementLabel}>{statement}</div>
            {Array.from({ length: (question.scale?.max ?? 5) - (question.scale?.min ?? 1) + 1 }).map((_, colIndex) => (
              <div key={colIndex} className={styles.radioCell}>
                <Radio 
                  value={`${rowIndex}-${colIndex}`}
                  checked={answers[rowIndex] === `${rowIndex}-${colIndex}`}
                  onChange={() => handleAnswerChange(rowIndex, `${rowIndex}-${colIndex}`)}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
